import { EXPRESS_SCRIPTS_CLIENT_ID } from '../steps';
import type { LegacySignupParams } from './legacy-params';
import { SignupRequestError } from './signup-api/signup-api';

/** How long the one quiet retry of a failed sponsor lookup waits. */
export const SPONSOR_LOOKUP_RETRY_DELAY_MS = 1000;

/**
 * Whether a failed sponsor lookup is worth one more try: a refusal that may
 * lift in a moment (429), a server or gateway failure (5xx), or a request that
 * never got an answer. "No such sponsor" and a malformed id are answers, not
 * blips, and a call with no backend will not start working on a retry.
 */
export function isRetryableSponsorFailure(error: unknown): boolean {
  if (!(error instanceof SignupRequestError)) return false;
  if (error.code === 'not_found' || error.code === 'invalid_request') return false;
  if (error.httpStatus === undefined) return error.kind === 'network';
  return error.httpStatus === 429 || error.httpStatus >= 500;
}

/**
 * Whether a link names an employer. One that does never continues without
 * its sponsor; only a link that names none goes on without one.
 */
export function linkNamesSponsor(clientId: string | undefined): boolean {
  return clientId !== undefined && clientId !== '';
}

/**
 * Whether a failed lookup means an Express Scripts link named a carrier the
 * client portal does not know. Legacy signup carried on as plain Express
 * Scripts then, so the flow drops the carrier and lets the member search
 * Express Scripts' carriers instead of stopping. Only a definite "no such
 * sponsor" counts: a refusal or outage still stops at the error screen.
 */
export function isUnknownExpressScriptsCarrier(
  params: Pick<LegacySignupParams, 'clientId' | 'subClientId'>,
  error: unknown,
): boolean {
  return (
    params.clientId === EXPRESS_SCRIPTS_CLIENT_ID &&
    params.subClientId !== undefined &&
    error instanceof SignupRequestError &&
    error.code === 'not_found'
  );
}

/** The link's query without its carrier, every other param kept in order. */
export function queryWithoutCarrier(query: string): string {
  const params = new URLSearchParams(query);
  params.delete('sub_client_id');
  return params.toString();
}
